import { z } from 'zod';

/**
 * Analytics & Metrics Schemas
 */

// Dashboard metrics
export const dashboardMetricsSchema = z.object({
  firstContactResolution: z.object({
    value: z.number().min(0).max(100), // percentage
    change: z.number(),
    changeType: z.enum(['increase', 'decrease']),
  }),
  customerEffortScore: z.object({
    value: z.number().min(1).max(5),
    change: z.number(),
    changeType: z.enum(['increase', 'decrease']),
  }),
  emotionalValueIndex: z.object({
    value: z.number().min(0).max(100),
    change: z.number(),
    changeType: z.enum(['increase', 'decrease']),
  }),
  supportROI: z.object({
    value: z.number(), // percentage
    retainedRevenue: z.number(),
  }),
});

// Sentiment over time
export const sentimentOverTimeSchema = z.object({
  dataPoints: z.array(
    z.object({
      date: z.coerce.date(),
      evi: z.number().min(0).max(100),
    }),
  ),
});

// Top frustration drivers
export const frustrationDriverSchema = z.object({
  topic: z.string(),
  mentions: z.number().int().nonnegative(),
  percentage: z.number().min(0).max(100),
  severity: z.enum(['low', 'medium', 'high']),
});

export const frustrationDriversSchema = z.object({
  drivers: z.array(frustrationDriverSchema),
});

// Emerging issues
export const emergingIssueSchema = z.object({
  topic: z.string(),
  mentions: z.number().int().nonnegative(),
  growthRate: z.number(), // percentage
});

export const emergingIssuesSchema = z.object({
  issues: z.array(emergingIssueSchema),
});

// Conversation metrics
export const conversationMetricsSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  firstContactResolution: z.boolean(),
  effortScore: z.number().min(1).max(5).nullable(),
  emotionalValueIndex: z.number().min(0).max(100).nullable(),
  totalMessages: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  totalCost: z.number().nonnegative(),
  agentsInvolved: z.array(z.string()),
  toolsUsed: z.array(z.string()),
  durationSeconds: z.number().int().nullable(),
  resolutionTimeSeconds: z.number().int().nullable(),
  calculatedAt: z.coerce.date(),
});

// FinOps metrics
export const finOpsMetricsSchema = z.object({
  totalSpend: z.object({
    value: z.number().nonnegative(),
    period: z.string(), // "MTD", "30d", etc.
    change: z.number(),
  }),
  resolvedConversations: z.object({
    value: z.number().int().nonnegative(),
    change: z.number(),
  }),
  avgCostPerResolution: z.object({
    value: z.number().nonnegative(),
    change: z.number(),
    changeType: z.enum(['increase', 'decrease']),
  }),
  dataEgressFees: z.object({
    value: z.number().nonnegative(),
    poweredBy: z.string(),
  }),
  spendOverTime: z.array(
    z.object({
      date: z.coerce.date(),
      spend: z.number().nonnegative(),
      limit: z.number().nonnegative(),
    }),
  ),
  usageBreakdown: z.array(
    z.object({
      conversationId: z.string(),
      date: z.coerce.date(),
      agentsInvolved: z.array(z.string()),
      tokens: z.number().int().nonnegative(),
      totalCost: z.number().nonnegative(),
    }),
  ),
});

// Analytics query parameters
export const analyticsQuerySchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
  granularity: z.enum(['hour', 'day', 'week', 'month']).default('day'),
});

// Export inferred types
export type DashboardMetrics = z.infer<typeof dashboardMetricsSchema>;
export type SentimentOverTime = z.infer<typeof sentimentOverTimeSchema>;
export type FrustrationDriver = z.infer<typeof frustrationDriverSchema>;
export type FrustrationDrivers = z.infer<typeof frustrationDriversSchema>;
export type EmergingIssue = z.infer<typeof emergingIssueSchema>;
export type EmergingIssues = z.infer<typeof emergingIssuesSchema>;
export type ConversationMetrics = z.infer<typeof conversationMetricsSchema>;
export type FinOpsMetrics = z.infer<typeof finOpsMetricsSchema>;
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;
